import React, { Fragment, useEffect, useState } from "react";
import ReviewsRatingIcon from "../../../../assets/images/svgs/reviewStarSvg.svg";
import useOutsideClick from "../../../../hooks/click-outside";
import useAttendee from "containers/attendee/useHooks";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import { useSelector } from "react-redux";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import noReviewFound from "assets/images/gifs/reviews-not-found.gif";

export interface CogDto {
  cogId: number;
  quantity: number;
}

export interface EventFeedbackDto {
  cogs: CogDto[];
  rating: number;
  comment: string;
  eventId: number;
}

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId?: number;
  hostId?: number;
  onReviewSubmitted?: () => void;
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  isOpen,
  onClose,
  eventId,
  hostId,
  onReviewSubmitted,
}) => {
  console.log(eventId, hostId);
  const { getAllCOG, addCOGReviewToEvent } = useAttendee();
  const [data, setData] = useState<any[]>([]);
  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState<string>("");
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [showRatingError, setShowRatingError] = useState<boolean>(false);
  const [showCommentError, setShowCommentError] = useState<boolean>(false);
  const [selectedCups, setSelectedCups] = useState<{ [key: number]: number }>(
    {}
  );
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const modalRef = useOutsideClick(onClose);
  console.log(hostId, "hostId");
  useEffect(() => {
    if (!isOpen) return;
    const fetchData = async () => {
      await getAllCOG(setData, {});
    };
    fetchData();
    // Reset error states when modal opens
    setShowRatingError(false);
    setShowCommentError(false);
  }, [isOpen]);

  const handleIncrease = (cupId: number) => {
    setSelectedCups((prev) => ({
      ...prev,
      [cupId]: (prev[cupId] || 0) + 1,
    }));
  };

  const handleDecrease = (cupId: number) => {
    setSelectedCups((prev) => {
      if (!prev[cupId]) return prev;
      const newQty = prev[cupId] - 1;
      if (newQty <= 0) {
        const { [cupId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [cupId]: newQty };
    });
  };

  const handleSubmit = async () => {
    if (!eventId && !hostId) return;

    // Validate mandatory fields
    let hasError = false;
    
    if (rating === 0) {
      setShowRatingError(true);
      hasError = true;
    } else {
      setShowRatingError(false);
    }
    
    if (!comment || comment.trim() === "") {
      setShowCommentError(true);
      hasError = true;
    } else {
      setShowCommentError(false);
    }
    
    if (hasError) {
      return;
    }

    const cogsPayload = Object.entries(selectedCups).map(
      ([cogId, quantity]) => ({
        cogId: Number(cogId),
        quantity,
      })
    );

    const payload: EventFeedbackDto = {
      cogs: cogsPayload,
      rating,
      comment,
      eventId,
    };
    try {
      await addCOGReviewToEvent(hostId, payload);
      onReviewSubmitted?.();
      onClose();
    } catch (error) {
      console.error("Error submitting review:", error);
    }
  };

  useEffect(() => {
    if (isInitialLoad && !isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, !data?.length]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{ zIndex: "calc(var(--z-modal) + 1)" }}
    >
      <div
        ref={modalRef}
        className={`bg-white px-6 py-8 rounded-lg shadow-lg w-[85%] h-[80%] md:h-[w-2/3] md:w-2/3 overflow-y-auto transform transition-transform duration-300 ${
          isOpen ? "scale-100" : "scale-95"
        }`}
      >
        <h2 className="text-2xl font-bold text-center mb-4">Add a Review</h2>
        <div className="flex flex-col items-center justify-center mb-4">
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <img
                key={star}
                src={ReviewsRatingIcon}
                alt="Star"
                className={`w-8 cursor-pointer ${
                  star <= rating ? "opacity-100" : "opacity-30"
                }`}
                onClick={() => {
                  setRating(star);
                  if (showRatingError) {
                    setShowRatingError(false);
                  }
                }}
              />
            ))}
          </div>
          {showRatingError && (
            <p className="text-red-500 text-sm mt-2">Please select a star rating</p>
          )}
        </div>

        <div className="mb-4">
          <label className="text-label text-base font-semibold mb-2">
            Add comments
          </label>
          <textarea
            className={`w-full p-3 rounded-lg border outline-none ${
              showCommentError ? "border-red-500" : "border-inputsBorderColor"
            }`}
            placeholder="Write here"
            rows={3}
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              if (showCommentError && e.target.value.trim()) {
                setShowCommentError(false);
              }
            }}
            maxLength={400}
          />
          {showCommentError && (
            <p className="text-red-500 text-sm mt-1">Please write a review comment</p>
          )}
          <div className="text-right text-sm text-gray-500 mt-1">
            {comment.length}/400
          </div>
        </div>
        {data && data.length > 0 && (
          <Fragment>
            <h3 className="text-lg font-semibold text-center mb-2">
              Choose the Cup of Gratitude
            </h3>
            <div className="flex justify-center flex-wrap gap-3">
              {data?.map((cup: any) => {
                const qty = selectedCups[cup.id] || 0;
                return (
                  <div
                    key={cup.id}
                    className={`bg-white rounded-2xl shadow-md hover:shadow-lg transition p-4 text-center w-40 flex flex-col items-center h-56 justify-between ${
                      qty > 0 ? "border-2 border-yellow-500" : ""
                    }`}
                  >
                    {/* Title at the top */}
                    <h3 className="text-blackie text-xs font-bold line-clamp-2 min-h-[32px]">
                      {cup.title}
                    </h3>

                    {/* Image and details in middle */}
                    <div className="flex flex-col items-center gap-2 flex-1">
                      <img
                        src={concatPathWithBackendUrl(
                          cup?.image?.fileDownloadUri
                        )}
                        alt={cup.title}
                        className="w-20 h-20 object-contain"
                      />
                      <span className="text-gray-700 font-medium text-sm whitespace-nowrap truncate w-full">
                        {cup?.name}
                      </span>
                      <span className="text-sm text-gray-500">
                        Price: ${cup?.price}
                      </span>
                    </div>

                    {/* Quantity Selector at the bottom */}
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        className="bg-gray-200 text-black w-[24px] h-[24px] rounded-full disabled:opacity-40"
                        onClick={() => handleDecrease(cup.id)}
                        disabled={qty === 0}
                      >
                        -
                      </button>
                      <span className="min-w-[24px] text-center">{qty}</span>
                      <button
                        className="bg-yellow-500 hover:bg-yellow-600 text-white w-[24px] h-[24px] rounded-full"
                        onClick={() => handleIncrease(cup.id)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <CustomMessageDisplay
              show={!isLoading && !isInitialLoad && !data.length}
              title="No Reviews Yet"
              slogan="Be the first to share your experience! Your review helps others discover great events."
              image={noReviewFound}
              imageAlt="No reviews found"
              className="py-16"
              imageClassName="w-[150px] h-[120px]"
              titleClassName="text-2xl font-bold text-blackie"
              sloganClassName="text-gray-500 text-base"
            />
          </Fragment>
        )}
        <div
          onClick={handleSubmit}
          className="flex justify-center py-3 rounded-full text-white bg-primaryBlueBg cursor-pointer mt-6 w-1/2 mx-auto"
        >
          <button className="text-center text-white bg-primaryBlueBg cursor-pointer">
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;
